import { Loading } from '@byecode/ui/components/Loading'
import { AnchorHashProvider } from '@lighthouse/block'
import { CenteredWrapper, useAtomAction, useAtomData } from '@lighthouse/shared'
import { useCallback, useEffect, useMemo } from 'react'
import { useUpdateEffect } from 'react-use'

import { pageSyncComponentsAtom } from '@/atoms/application/state'
import { clearPageStackBlockValuesAtom, initContainerRuntimeStateAtom } from '@/atoms/page/action'
import { pageBlocksAtom, pageStackAtomFamily } from '@/atoms/page/state'
import { PageProvider, PageStackProvider } from '@/contexts/PageContext'
import { ActionRuntimeProvider } from '@/hooks/useActionTrigger'
import { useCurrentBreakPointId } from '@/hooks/useApplication'

import { PageContent } from './PageContentV2'

interface PageContainerProps {
    pageId: string
    loading?: boolean
    stackId: string
    onFreshPage?: () => void
}

export const PageContainer = ({ pageId, stackId, loading, onFreshPage }: PageContainerProps) => {
    const breakPointId = useCurrentBreakPointId()
    const pageBlocks = useAtomData(pageBlocksAtom(pageId))
    const stackHash = useAtomData(
        pageStackAtomFamily(stackId),
        useCallback(s => s?.hash, [])
    )
    const syncComponents = useAtomData(pageSyncComponentsAtom)
    const { run: initContainerRuntimeState } = useAtomAction(initContainerRuntimeStateAtom)
    const { run: clearPageStackBlockValues } = useAtomAction(clearPageStackBlockValuesAtom)

    // 切换断点时更新runtime state
    useUpdateEffect(() => {
        initContainerRuntimeState({
            stackId,
            blocks: pageBlocks,
            syncComponents,
            isReplaceExist: true
        })
    }, [breakPointId])

    useEffect(() => {
        return () => {
            clearPageStackBlockValues(stackId)
        }
    }, [clearPageStackBlockValues, stackId])

    const pageContextValue = useMemo(() => ({ pageId, refreshPage: onFreshPage }), [pageId, onFreshPage])

    return (
        <PageProvider value={pageContextValue}>
            <PageStackProvider value={stackId}>
                <ActionRuntimeProvider>
                    <AnchorHashProvider value={stackHash}>
                        {loading ? (
                            <CenteredWrapper style={{ padding: '80px 0' }}>
                                <Loading size={32} />
                            </CenteredWrapper>
                        ) : (
                            <PageContent />
                        )}
                    </AnchorHashProvider>
                </ActionRuntimeProvider>
            </PageStackProvider>
        </PageProvider>
    )
}
